import {
	Component,
	EventEmitter,
	Input,
	Output,
	OnInit,
	OnChanges,
	SimpleChanges,
	ChangeDetectorRef,
} from '@angular/core';
import { AttestationPresenceThematiqueSummarySearchParams } from '@app/models/attestation-presence-thematique-summary';
import { AttestationPresenceThematiqueSummary } from '@app/models/attestation-presence-thematique-summary/attestation-presence-thematique-summary';
import { AttestationPresenceThematiqueSummaryService } from '@app/models/attestation-presence-thematique-summary/attestation-presence-thematique-summary.service';
import { FormationThematique } from '@app/models/formation-thematique/formation-thematique';
import {
	FormationThematiqueService,
	FormateurStagiaireSignature,
	BackendStagiaireData,
} from '@app/models/formation-thematique/formation-thematique.service';
import { AttestationPresenceThematiqueService } from '@app/models/attestation-presence-thematique/attestation-presence-thematique.service';
import { ErrorService } from '@app/services/error.service';
import { TranslateService } from '@ngx-translate/core';

// Interface for transformed group
interface TransformedFormateurGroup {
	date: number;
	period: 'afternoon' | 'morning';
	stagiaires: FormateurStagiaireSignature[];
}

// Interface for display row
interface ThematiqueSummaryRow {
	thematique: FormationThematique;
	summary?: AttestationPresenceThematiqueSummary;
	hasDocument: boolean;
}

@Component({
	selector: 'app-atom-formation-thematique-table-summary-documents',
	templateUrl:
		'./atom-formation-thematique-table-summary-documents.component.html',
	styleUrls: [
		'./atom-formation-thematique-table-summary-documents.component.less',
	],
})
export class AtomFormationThematiqueTableSummaryDocumentsComponent
	implements OnInit, OnChanges
{
	@Input() title: string;
	@Input() thematiques: FormationThematique[] = [];
	@Input() loading = false;

	@Output() emitGenerate = new EventEmitter<string>(); // thematique ID
	@Output() emitView = new EventEmitter<string>(); // summary ID
	@Output() emitDelete = new EventEmitter<string>(); // summary ID
	@Output() emitDownload = new EventEmitter<{
		summaryId: string;
		filename: string;
	}>();

	displayRows: ThematiqueSummaryRow[] = [];

	// Edit modal state
	editModalVisible = false;
	editModalLoading = false;
	editModalThematique: FormationThematique;
	stagiaireGroups: TransformedFormateurGroup[] = [];
	expandedGroups = new Set<string>();

	// Delay modal (nested)
	delayModalVisible = false;
	delayModalLoading = false;
	delayModalTitle = '';
	delayValue = 0;
	selectedStagiaire: FormateurStagiaireSignature;
	selectedGroup: TransformedFormateurGroup;

	// Delete confirmation
	deleteModalVisible = false;
	deleteModalLoading = false;
	selectedAttestationForDelete: {
		attestationId: string;
		stagiaireName: string;
	};

	constructor(
		public translateService: TranslateService,
		private summaryService: AttestationPresenceThematiqueSummaryService,
		private formationThematiqueService: FormationThematiqueService,
		private attestationPresenceThematiqueService: AttestationPresenceThematiqueService,
		private errorService: ErrorService,
		private cdr: ChangeDetectorRef
	) {}

	async ngOnInit(): Promise<void> {
		await this.refresh();
	}

	async ngOnChanges(changes: SimpleChanges): Promise<void> {
		if (changes.thematiques && !changes.thematiques.firstChange) {
			await this.refresh();
		}
	}

	async refresh(): Promise<void> {
		if (!this.thematiques || this.thematiques.length === 0) {
			this.displayRows = [];
			return;
		}

		// Filter thematiques that have signature enabled (need_sign === true)
		const thematiqueWithSignature = this.thematiques.filter(
			(t) => t.props.need_sign === true
		);

		if (thematiqueWithSignature.length === 0) {
			this.displayRows = [];
			return;
		}

		this.loading = true;

		try {
			// Fetch summaries for thematiques with signature enabled
			const thematiqueIds = thematiqueWithSignature.map((t) => t.getId());
			const result = await this.summaryService.listAsAdmin(
				new AttestationPresenceThematiqueSummarySearchParams({
					thematique: thematiqueIds,
					_populate: ['document', 'formateur'],
				}).toObject()
			);

			// Create a map of thematique ID to summary
			const summaryMap = new Map<
				string,
				AttestationPresenceThematiqueSummary
			>();
			result.items.forEach((summary) => {
				const thematiqueId =
					typeof summary.props.thematique === 'string'
						? summary.props.thematique
						: (
								summary.props.thematique as FormationThematique
						  ).getId();
				summaryMap.set(thematiqueId, summary);
			});

			// Build display rows only for thematiques with signature enabled
			this.displayRows = thematiqueWithSignature.map((thematique) => {
				const summary = summaryMap.get(thematique.getId());
				return {
					thematique,
					summary,
					hasDocument: !!(summary && summary.props.document),
				};
			});
		} catch (error) {
			console.error('Error loading summaries:', error);
			this.displayRows = thematiqueWithSignature.map((thematique) => ({
				thematique,
				summary: null,
				hasDocument: false,
			}));
		} finally {
			this.loading = false;
		}
	}

	trackByThematique(_: number, row: ThematiqueSummaryRow): string {
		return row.thematique.getId();
	}

	getThematiqueName(row: ThematiqueSummaryRow): string {
		return row.thematique.props.name || '-';
	}

	getDocumentLabel(row: ThematiqueSummaryRow): string {
		if (!row.hasDocument) {
			return '-';
		}
		const doc = row.summary.props.document as any;
		const label = doc.props ? doc.props.label : doc.label;
		return label || `Attestation présence - ${row.thematique.props.name}`;
	}

	getDocumentStatus(row: ThematiqueSummaryRow): string | null {
		if (!row.hasDocument) {
			return null;
		}
		const doc = row.summary.props.document as any;
		return doc.status || 'created';
	}

	handleGenerate(row: ThematiqueSummaryRow): void {
		// Emit thematique ID for generation
		this.emitGenerate.emit(row.thematique.getId());
	}

	handleView(row: ThematiqueSummaryRow): void {
		if (row.summary) {
			this.emitView.emit(row.summary.getId());
		}
	}

	handleDelete(row: ThematiqueSummaryRow): void {
		if (row.summary) {
			this.emitDelete.emit(row.summary.getId());
		}
	}

	handleDownload(row: ThematiqueSummaryRow): void {
		if (row.summary) {
			const filename = this.getDocumentLabel(row);
			this.emitDownload.emit({
				summaryId: row.summary.getId(),
				filename,
			});
		}
	}

	// ============================================
	// Edit Modal Methods
	// ============================================

	async openEditModal(row: ThematiqueSummaryRow): Promise<void> {
		this.editModalThematique = row.thematique;
		this.editModalVisible = true;
		this.editModalLoading = true;
		this.stagiaireGroups = [];
		this.expandedGroups.clear();
		this.cdr.detectChanges();

		await this.loadStagiaireGroups(row.thematique.getId());
	}

	closeEditModal(): void {
		this.editModalVisible = false;
		this.editModalLoading = false;
		this.editModalThematique = null;
		this.stagiaireGroups = [];
		this.expandedGroups.clear();
	}

	async loadStagiaireGroups(thematiqueId: string): Promise<void> {
		try {
			const formateurMetadata =
				await this.formationThematiqueService.getAdminMetadata(
					thematiqueId
				);

			// Transform backend data for stagiaire signatures
			const presenceDates =
				formateurMetadata.attestation_presence_dates || [];

			this.stagiaireGroups = presenceDates.map((group) => {
				const signatureDate = new Date(group.signatureDateTime);
				const timestamp = signatureDate.getTime();

				// Transform each stagiaire
				const stagiaires = group.stagiaires || [];
				const transformedStagiaires: FormateurStagiaireSignature[] =
					stagiaires.map((stagiaireData: BackendStagiaireData) => {
						const att = stagiaireData.attestation;
						const isExcused = !!(att && att.excused);
						return {
							stagiaire_id: stagiaireData.stagiaire._id,
							stagiaire_name: `${stagiaireData.stagiaire.first_name} ${stagiaireData.stagiaire.last_name}`,
							signed: !!att && !isExcused,
							excused: isExcused,
							signed_at: att?.created_at,
							delay: att?.delay,
							attestation_id: att?._id,
						};
					});

				return {
					date: timestamp,
					period: group.period,
					stagiaires: transformedStagiaires,
				};
			});

			// Sort groups by date ascending
			this.stagiaireGroups.sort((a, b) => a.date - b.date);
		} catch (err) {
			this.errorService.handle(err);
			this.stagiaireGroups = [];
		} finally {
			this.editModalLoading = false;
			this.cdr.detectChanges();
		}
	}

	// ============================================
	// Group Expand/Collapse Methods
	// ============================================

	getGroupKey(group: TransformedFormateurGroup): string {
		return `${group.date}-${group.period}`;
	}

	isGroupExpanded(group: TransformedFormateurGroup): boolean {
		return this.expandedGroups.has(this.getGroupKey(group));
	}

	toggleGroup(group: TransformedFormateurGroup): void {
		const key = this.getGroupKey(group);
		if (this.expandedGroups.has(key)) {
			this.expandedGroups.delete(key);
		} else {
			this.expandedGroups.add(key);
		}
		this.cdr.detectChanges();
	}

	// ============================================
	// Delay Modal Methods
	// ============================================

	openDelayModal(
		stagiaire: FormateurStagiaireSignature,
		group: TransformedFormateurGroup
	): void {
		this.selectedStagiaire = stagiaire;
		this.selectedGroup = group;

		if (stagiaire.signed) {
			this.delayModalTitle = this.translateService.instant(
				'presence-signature_modal-edit-absence-title'
			);
			this.delayValue = stagiaire.delay || 0;
		} else {
			this.delayModalTitle = this.translateService.instant(
				'presence-signature_modal-sign-title'
			);
			this.delayValue = 0;
		}

		this.delayModalVisible = true;
	}

	closeDelayModal(): void {
		this.delayModalVisible = false;
		this.delayModalLoading = false;
		this.selectedStagiaire = null;
		this.selectedGroup = null;
		this.delayValue = 0;
	}

	async submitDelayModal(): Promise<void> {
		try {
			this.delayModalLoading = true;

			if (this.selectedStagiaire.signed) {
				// Update delay for signed attestation
				await this.attestationPresenceThematiqueService.updateAsAdmin(
					this.selectedStagiaire.attestation_id,
					{ delay: this.delayValue }
				);
			} else {
				// Create attestation for unsigned stagiaire
				await this.attestationPresenceThematiqueService.signDelayAsAdmin(
					this.editModalThematique.getId(),
					this.selectedGroup.date,
					this.selectedGroup.period,
					this.selectedStagiaire.stagiaire_id,
					this.delayValue
				);
			}

			// Reload stagiaire data
			await this.loadStagiaireGroups(this.editModalThematique.getId());
			this.closeDelayModal();
			this.cdr.detectChanges();
		} catch (err) {
			this.delayModalLoading = false;
			this.errorService.handle(err);
		}
	}

	// ============================================
	// Delete Modal Methods
	// ============================================

	openDeleteModal(stagiaire: FormateurStagiaireSignature): void {
		if (!stagiaire.attestation_id) return;

		this.selectedAttestationForDelete = {
			attestationId: stagiaire.attestation_id,
			stagiaireName: stagiaire.stagiaire_name,
		};
		this.deleteModalVisible = true;
	}

	closeDeleteModal(): void {
		this.deleteModalVisible = false;
		this.deleteModalLoading = false;
		this.selectedAttestationForDelete = null;
	}

	async confirmDeleteAttestation(): Promise<void> {
		if (!this.selectedAttestationForDelete) return;

		try {
			this.deleteModalLoading = true;

			await this.attestationPresenceThematiqueService.removeAsAdmin(
				this.selectedAttestationForDelete.attestationId
			);

			// Reload stagiaire data
			await this.loadStagiaireGroups(this.editModalThematique.getId());
			this.closeDeleteModal();
			this.cdr.detectChanges();
		} catch (err) {
			this.deleteModalLoading = false;
			this.errorService.handle(err);
		}
	}

	// ============================================
	// Helper Methods
	// ============================================

	getPeriodLabel(period: 'afternoon' | 'morning'): string {
		return period === 'morning' ? 'Matin' : 'Après-midi';
	}

	getMissingSignaturesCount(group: TransformedFormateurGroup): number {
		return group.stagiaires.filter((s) => !s.signed).length;
	}

	trackByGroup = (_: number, group: TransformedFormateurGroup): string => {
		return this.getGroupKey(group);
	};

	trackByStagiaire = (
		_: number,
		stagiaire: FormateurStagiaireSignature
	): string => {
		return stagiaire.stagiaire_id;
	};
}
